import { normalizePhotoLabels, photoLabelFreeFormId, PHOTO_LABEL_NAME_MAX_LENGTH } from "@rebuild/shared";
import type { ResponsiveNotePhotoLabel } from "@rebuild/shared/models";
import { X } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Combobox,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { useGetEstimatePhotoLabelsQuery } from "@/hooks/queries/responsive-note-photos";
import { appSelector } from "@/redux/selectors/app.selector";
import { useSelector } from "@/redux/store";
import { PhotoToolbarPanelHeader } from "./PhotoToolbarPanelHeader";

interface PhotoLabelPickerProps {
  estimateId: string | undefined;
  photoCount: number;
  onClose: () => void;
  onApply: (labels: ResponsiveNotePhotoLabel[]) => void;
  isPending: boolean;
  /** When set, the picker is disabled and this explains why (color alone must not carry the meaning). */
  disabledReason?: string;
}

export function PhotoLabelPicker({ estimateId, photoCount, onClose, onApply, isPending, disabledReason }: PhotoLabelPickerProps) {
  const orgPresets = useSelector(appSelector.organizationPhotoLabels);
  const { data: estimateLabels } = useGetEstimatePhotoLabelsQuery(estimateId);
  // Org presets first so a curated label's id and casing win the dedupe over an ad-hoc duplicate.
  const orgLabels = useMemo(() => normalizePhotoLabels([...orgPresets, ...(estimateLabels ?? [])]), [estimateLabels, orgPresets]);
  const [selectedLabels, setSelectedLabels] = useState<ResponsiveNotePhotoLabel[]>([]);
  const [inputValue, setInputValue] = useState("");

  const isDisabled = Boolean(disabledReason) || isPending;

  const options = useMemo(() => {
    const chosenNames = new Set(selectedLabels.map((label) => label.name.trim().toLowerCase()));
    const presets = orgLabels.filter((label) => !chosenNames.has(label.name.trim().toLowerCase()));

    // Free-form: offer whatever was typed when it doesn't already match a preset or a chip.
    const typed = inputValue.trim();
    const typedKey = typed.toLowerCase();
    const matchesPreset = orgLabels.some((label) => label.name.trim().toLowerCase() === typedKey);
    if (!typed || matchesPreset || chosenNames.has(typedKey)) {
      return presets;
    }

    return [...presets, { id: `new:${typed}`, name: typed }];
  }, [orgLabels, selectedLabels, inputValue]);

  const handleValueChange = (next: ResponsiveNotePhotoLabel[] | null) => {
    // Free-form entries arrive with a placeholder id; mint a real one so the stored shape is uniform.
    setSelectedLabels(
      (next ?? []).map((label) => {
        if (!label.id.startsWith("new:")) return label;
        const name = label.name.slice(0, PHOTO_LABEL_NAME_MAX_LENGTH);
        // Derived from the name, so the same word typed here and on a phone is one tag, not two.
        return { id: photoLabelFreeFormId(name), name };
      }),
    );
    setInputValue("");
  };

  const handleApply = () => {
    if (selectedLabels.length === 0) return;
    onApply(selectedLabels);
    setSelectedLabels([]);
    setInputValue("");
  };

  const handleRemove = (labelId: string) => {
    setSelectedLabels((current) => current.filter((label) => label.id !== labelId));
  };

  const hasStagedLabels = selectedLabels.length > 0;

  return (
    <div className="flex flex-col gap-1.5" id="photo-toolbar-labels-panel">
      <PhotoToolbarPanelHeader title={`Labels for ${photoCount} photo${photoCount === 1 ? "" : "s"}`} onClose={onClose} />
      <Combobox
        multiple
        autoHighlight
        items={options}
        itemToStringValue={(option: ResponsiveNotePhotoLabel) => option.name}
        value={selectedLabels}
        onValueChange={handleValueChange}
        inputValue={inputValue}
        onInputValueChange={setInputValue}
        disabled={isDisabled}
      >
        <ComboboxChips className="min-h-8 w-full border-gray-700 bg-transparent text-white transition-colors focus-within:border-gray-500">
          <ComboboxChipsInput
            placeholder="Type a label, press Enter"
            aria-label="Labels to apply"
            disabled={isDisabled}
            maxLength={PHOTO_LABEL_NAME_MAX_LENGTH}
            className="text-white placeholder:text-gray-400"
          />
        </ComboboxChips>
        <ComboboxContent
          side="top"
          align="start"
          className="w-(--anchor-width) border border-gray-700 bg-gray-900 text-white ring-gray-700"
        >
          <ComboboxEmpty className="text-gray-400">Type to add a new label</ComboboxEmpty>
          <ComboboxList className="max-h-60 overflow-y-auto">
            {(option: ResponsiveNotePhotoLabel) => (
              <ComboboxItem
                key={option.id}
                value={option}
                className="text-gray-200 data-highlighted:bg-gray-800 data-highlighted:text-white"
              >
                {option.id.startsWith("new:") ? `Add "${option.name}"` : option.name}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      {hasStagedLabels && (
        <ul className="flex flex-wrap gap-1.5" aria-label="Labels to apply">
          {selectedLabels.map((label) => (
            <li key={label.id} className="flex h-6 items-center gap-1 rounded-full bg-gray-800 pr-1 pl-2.5 text-xs font-medium text-white">
              {label.name}
              <button
                type="button"
                aria-label={`Remove label ${label.name}`}
                disabled={isDisabled}
                className="rounded-full p-0.5 text-gray-400 hover:text-white disabled:pointer-events-none"
                onClick={() => handleRemove(label.id)}
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {hasStagedLabels && <span className="text-xs text-gray-400">Replaces the labels already on the selected photos.</span>}
      {disabledReason && <span className="text-xs text-gray-400">{disabledReason}</span>}
      <Button className="h-8 w-full" onClick={handleApply} disabled={isDisabled || !hasStagedLabels} title={disabledReason}>
        {isPending ? "Applying…" : "Apply"}
      </Button>
    </div>
  );
}
